import React, { useEffect } from "react";
import { faBookOpen, faCopy, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Button, Col, ListGroupItem, Row, Stack } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import IconButton from "./IconButton";
import { Link } from "react-router-dom";

const APP_LINKS = {
  family: {
    ios: "https://apps.apple.com/us/app/andgo-family/id6463176307",
    android: "https://play.google.com/store/apps/details?id=com.selflyft.app&hl=en_US",
  },
  caseManagement: {
    ios: "https://apps.apple.com/us/app/andgo-case-management/id6475694301",
    android: "https://play.google.com/store/apps/details?id=com.selflyftcm.app",
  },
};

function StickyMenu() {
  const [open, setOpen] = useState<boolean>(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState<boolean>(false);
  const [showMobileAppLinks, setShowMobileAppLinks] = useState<boolean>(false);
  const [releaseNotes, setReleaseNotes] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const copyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const startPendoGuide = () => {
    if (window.pendo) {
      window.pendo.showGuideById("cHxrbegS7-r4uOCAsJk4MQRoCqU");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
        setShowReleaseNotes(false);
        setShowMobileAppLinks(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);

  const loadReleaseNotes = async () => {
    if (releaseNotes) return;

    const res = await fetch("/release.md");
    let text = await res.text();

    // Remove HTML comments
    text = text.replace(/<!--[\s\S]*?-->/g, "");

    setReleaseNotes(text);
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(url);

    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    copyTimeoutRef.current = setTimeout(() => {
      setCopiedLink(null);
    }, 1500);
  };

  return (
    <div
      ref={menuRef}
      className={`fab-wrapper app-primary-alt-color ${open && "open"} ${showReleaseNotes && "release"}`}
      onClick={() => {
        setOpen(!open);
        setShowReleaseNotes(false);
        setShowMobileAppLinks(false);
      }}
    >
      <Button variant="transparent" className={`text-white w-100 ${!open && "fab-button"}`}>
        <Row>
          <Col xs={open ? 10 : 0} className="text-start">
            {open && <FontAwesomeIcon icon={faBookOpen} className="me-2" />}
            {open && "AndGo Support"}
          </Col>
          <Col xs={open ? 2 : 12} className={open ? "text-end" : "text-center"}>
            <FontAwesomeIcon icon={open ? faTimes : faBookOpen} className={open ? "" : ""} />
          </Col>
        </Row>
      </Button>
      <Stack direction="vertical" className="fab-menu p-0 mx-2 mb-2 rounded-3" gap={1} onClick={(e) => e.stopPropagation()}>
        <ListGroupItem
          variant="light"
          className="py-2 ps-2 cursor-pointer"
          onClick={async () => {
            await loadReleaseNotes();
            setShowReleaseNotes((prev) => !prev);
          }}
        >
          {`Release Notes ${localStorage.getItem("appVersion")}`}
        </ListGroupItem>
        <div className={`release-notes app-secondary-color-inverse ${showReleaseNotes && "open"}`}>
          {releaseNotes && <ReactMarkdown children={releaseNotes} />}
        </div>
        <ListGroupItem
          variant="light"
          className="py-2 ps-2 cursor-pointer"
          onClick={async () => {
            setShowMobileAppLinks((prev) => !prev);
          }}
        >
          Mobile Apps
        </ListGroupItem>
        <div className={`release-notes app-primary-color-inverse ${showMobileAppLinks && "open"}`}>
          <h6>AndGo Family</h6>
          <Row className="align-items-center">
            <Col xs={showReleaseNotes ? 6 : 8} className="ms-3">
              <Link to={APP_LINKS.family.ios} target="_blank" className="app-header-text">
                Apple App Store
              </Link>
            </Col>
            <Col xs="auto">
              <IconButton
                icon={faCopy}
                color="#5a7082"
                tooltipText={copiedLink === APP_LINKS.family.ios ? "Copied" : "Copy Link"}
                onClickButton={() => copyToClipboard(APP_LINKS.family.ios)}
              />
            </Col>
          </Row>
          <Row className="align-items-center">
            <Col xs={showReleaseNotes ? 6 : 8} className="ms-3">
              <Link to={APP_LINKS.family.android} target="_blank" className="app-header-text">
                Google Play Store
              </Link>
            </Col>
            <Col xs="auto">
              <IconButton
                icon={faCopy}
                color="#5a7082"
                tooltipText={copiedLink === APP_LINKS.family.android ? "Copied" : "Copy Link"}
                onClickButton={() => copyToClipboard(APP_LINKS.family.android)}
              />
            </Col>
          </Row>
          <hr></hr>
          <h6 className="mt-3">AndGo Case Management</h6>
          <Row className="align-items-center">
            <Col xs={showReleaseNotes ? 6 : 8} className="ms-3">
              <Link to={APP_LINKS.caseManagement.ios} target="_blank" className="app-header-text">
                Apple App Store
              </Link>
            </Col>
            <Col xs="auto">
              <IconButton
                icon={faCopy}
                color="#5a7082"
                tooltipText={copiedLink === APP_LINKS.caseManagement.ios ? "Copied" : "Copy Link"}
                onClickButton={() => copyToClipboard(APP_LINKS.caseManagement.ios)}
              />
            </Col>
          </Row>
          <Row className="align-items-center">
            <Col xs={showReleaseNotes ? 6 : 8} className="ms-3">
              <Link to={APP_LINKS.caseManagement.android} target="_blank" className="app-header-text">
                Google Play Store
              </Link>
            </Col>
            <Col xs="auto">
              <IconButton
                icon={faCopy}
                color="#5a7082"
                tooltipText={copiedLink === APP_LINKS.caseManagement.android ? "Copied" : "Copy Link"}
                onClickButton={() => copyToClipboard(APP_LINKS.caseManagement.android)}
              />
            </Col>
          </Row>
        </div>
        <ListGroupItem
          variant="light"
          className="py-2 ps-2 cursor-none"
          onClick={async () => {
            startPendoGuide();
          }}
        >
          Onboarding (Coming Soon)
        </ListGroupItem>
      </Stack>
    </div>
  );
}

export default StickyMenu;
